import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DocumentTemplateId } from "../constants/documentTemplates";
import type { TemplateFormValues } from "../utils/templateForm";

/** A form already filed in this project, offered as a starting point. */
export interface SavedTemplateForm {
  id: number;
  documentNumber: number;
  documentName: string;
  values: TemplateFormValues;
}

/** Enough to pick from without turning the popover into an archive browser. */
const RECENT_LIMIT = 25;

/**
 * The project's most recent forms of one template — what "Choose pre-filled
 * form" copies from, and what Import pulls a single field out of.
 *
 * Only forms whose PDF was actually filed are offered: a row without a
 * `document_id` is a reservation whose save did not finish, so its values were
 * never part of a document.
 */
export function useSavedTemplateForms(
  projectId: string | null,
  templateId: DocumentTemplateId,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["template-forms", projectId, templateId],
    enabled: enabled && projectId !== null,
    queryFn: async ({ signal }): Promise<SavedTemplateForm[]> => {
      const { data, error } = await supabase
        .from("document_forms")
        .select("id, document_number, document_name, values")
        .eq("project_id", Number(projectId))
        .eq("template_id", templateId)
        .not("document_id", "is", null)
        .order("document_number", { ascending: false })
        .limit(RECENT_LIMIT)
        .abortSignal(signal);
      if (error) throw new Error(error.message);

      return (data ?? []).map((row) => ({
        id: row.id,
        documentNumber: row.document_number,
        documentName: row.document_name,
        values: (row.values ?? {}) as TemplateFormValues,
      }));
    },
  });
}
